/**
 * `<WaitTips>` — while someone waits, how long it has been and one thing
 * worth knowing (#814).
 *
 * Rayhan, 30 Sep: _"use the window to show time count but also use that time
 * to show us features of Mangrove HQ features. Few lines on them how to use
 * it. Benefits it will bring etc. ... when claude thinks or is doing work.
 * Instead of waiting we can watch our own marketing."_
 *
 * Presentational: the app passes its own tips (HQ keeps its list in one file,
 * `apps/ops/src/lib/wait-tips.ts`), so a client app or the learner app could
 * show its own without touching this component.
 *
 * THE COUNT goes up, never down (the waking notice's rule: an elapsed count is
 * always true; a promise of "30 seconds" is not). One tip at a time, changing
 * every few seconds, starting at a different one each wait so a short wait
 * does not always show the same tip.
 *
 * NOT ANNOUNCED. Tips change on a timer; read aloud they would talk over the
 * thing the person is waiting for. The region is `aria-live="off"`, and a
 * screen reader reaches the current tip by moving to it.
 *
 * TAKE ME THERE (#828). A tip that names a place (`go`) can offer a button.
 * The page is waiting for the server, so the button cannot open anything yet:
 * the caller keeps the choice and opens it once the server answers, and this
 * component stops turning and says so.
 */
import * as React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '../lib/utils.js';

export interface WaitTip {
  /** The feature, in a few words: "File a spec". */
  title: string;
  /** Two or three short sentences: what it does, how to use it, what it gets you. */
  body: string;
  /** Where to find it: "Home → File a spec". */
  where?: string;
  /** #828: where "Take me there" goes, and what to call it while it waits. */
  go?: { href: string; name: string };
}

export interface WaitTipsProps {
  tips: readonly WaitTip[];
  /** Show the elapsed count in this component (off when the caller shows one). */
  showElapsed?: boolean;
  /** How long each tip stays up. */
  everyMs?: number;
  /** Which tip to start on; defaults to a random one each wait. */
  startAt?: number;
  /** #828: offered on a tip with `go`. The caller opens it when it can. */
  onGo?: (tip: WaitTip) => void;
  /** #828: the tip whose place was chosen; shown alone, with the promise. */
  chosen?: WaitTip | null;
  className?: string;
}

export function WaitTips({
  tips: given,
  showElapsed = false,
  everyMs = 9000,
  startAt,
  onGo,
  chosen,
  className,
}: WaitTipsProps): React.JSX.Element | null {
  // The list is read once, when the wait starts (#828): one ordered for this
  // person can be rebuilt while they wait, and the tip being read would jump.
  const [frozen] = React.useState(given);
  const tips = frozen.length > 0 ? frozen : given;
  const [started] = React.useState(() => Date.now());
  const [first] = React.useState(
    () => startAt ?? (tips.length > 0 ? Math.floor(Math.random() * tips.length) : 0),
  );
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (tips.length === 0) return null;
  const elapsedMs = Math.max(0, now - started);
  const index = (first + Math.floor(elapsedMs / everyMs)) % tips.length;
  const tip = chosen ?? tips[index]!;

  return (
    <div
      className={cn('rounded-md border bg-muted/40 p-3 text-left', className)}
      role="note"
      aria-live="off"
      data-testid="wait-tips"
      data-tip-index={index}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1 font-medium uppercase tracking-wide">
          <Sparkles className="h-3 w-3" aria-hidden />
          While you wait
        </span>
        {showElapsed ? (
          <span className="tabular-nums" data-testid="wait-tips-elapsed">
            {Math.floor(elapsedMs / 1000)}s
          </span>
        ) : null}
      </div>
      <p className="text-sm font-medium text-foreground" data-testid="wait-tip-title">
        {tip.title}
      </p>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{tip.body}</p>
      {tip.where ? <p className="mt-1 text-[11px] font-medium text-primary">{tip.where}</p> : null}
      {chosen?.go ? (
        <p className="mt-2 text-xs font-medium text-foreground" data-testid="wait-tip-chosen">
          We will open {chosen.go.name} as soon as the server answers.
        </p>
      ) : onGo && tip.go ? (
        <button
          type="button"
          onClick={() => onGo(tip)}
          className="mt-2 rounded-md border px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="wait-tip-go"
        >
          Take me there
        </button>
      ) : null}
    </div>
  );
}
